import type { ApolloClient } from "@apollo/client";
import type { SelectOption } from "@outpave/ds";
import {
  GET_ALLOCATIONS_INFINITE_SCROLL,
  GET_MY_ALLOCATIONS,
} from "graphql/allocation";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";

/** An allocation type as a picker or filter carries it. */
export interface AllocationType {
  key: string;
  name: string;
}

export interface AllocationNode {
  id: string;
  allocationCode: string;
  allocationType: { key: string; name: string };
  level: number;
  editable: boolean;
  visible: boolean;
  parentAllocationId: string | null;
  parentAllocationName: string | null;
  cleanedSk: string | null;
}

export interface AllocationPage {
  nodes: AllocationNode[];
  /** Recent picks are a flat, newest-first list, so they carry no tree depth. */
  recent: boolean;
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface AllocationPageArgs {
  query: string;
  after: string | null;
  allocationType?: { key: string; name: string } | null;
  /** Admin and report pickers browse everything, hidden included, so they never open on recents. */
  showAll?: boolean;
  /** The user asked to list every allocation the picker allows, rather than their recent picks. */
  browseAll?: boolean;
  variables?: Record<string, unknown>;
}

/** A pick opens on the user's recent allocations; anything else is found by search. */
export async function fetchAllocationPage(
  client: ApolloClient<object>,
  {
    query,
    after,
    allocationType,
    showAll,
    browseAll,
    variables,
  }: AllocationPageArgs,
): Promise<AllocationPage> {
  const searchTerm = query.trim();
  const typeVariable = allocationType
    ? { allocationType: { key: allocationType.key, name: allocationType.name } }
    : {};

  if (!searchTerm && !showAll && !browseAll) {
    const { data } = await client.query({
      query: GET_MY_ALLOCATIONS,
      variables: { ...typeVariable, scope: "RECENT" },
      fetchPolicy: "network-only",
    });
    return {
      nodes: data?.myAllocations ?? [],
      recent: true,
      hasNextPage: false,
      endCursor: null,
    };
  }

  const { data } = await client.query({
    query: GET_ALLOCATIONS_INFINITE_SCROLL,
    variables: {
      first: DEFAULT_PAGINATION_SIZE,
      after: after ?? undefined,
      searchTerm: searchTerm || undefined,
      ...typeVariable,
      ...(showAll ? { showAll: true } : {}),
      ...variables,
    },
    fetchPolicy: "network-only",
  });
  const connection = data?.allocationsInfiniteScroll;
  return {
    nodes: (connection?.edges ?? [])
      .map((edge: { node: AllocationNode | null }) => edge.node)
      .filter((node: AllocationNode | null): node is AllocationNode => !!node),
    recent: false,
    hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
    endCursor: connection?.pageInfo?.endCursor ?? null,
  };
}

/** A child names its ancestors, so a job reads as "Acme Corp" under "Phase 2"; a top-level one names its type. */
export function allocationCaption(node: AllocationNode): string | undefined {
  if (node.level > 0 && node.cleanedSk) return node.cleanedSk;
  return node.allocationType?.name || undefined;
}

export function allocationOption(
  node: AllocationNode,
  recent: boolean,
): SelectOption {
  return {
    id: node.id,
    label: node.allocationCode || node.id,
    caption: allocationCaption(node),
    depth: recent ? 0 : (node.level ?? 0),
  };
}
